// IMPORTANT: This component is essentially the same as the horizontal-help-popup.tsx
// with a few minor changes like the badge and intl. We are building this separate
// because later in this project we might be replacing this with the
// @atlaskit/help component and changing how it functions and where it mounts (aside/panel)
import { useState, useCallback, useMemo, useContext, memo } from 'react';

import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import { Help } from '@atlaskit/navigation-system/top-nav-items';
import { Popup, TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { DevToolsContext } from 'src/components/dev-tools-loader/context';
import { useBlogs } from 'src/components/navigation/src/components/blog-fetch';
import HelpMenu from 'src/components/navigation/src/components/help-menu';
import { messages } from 'src/components/navigation/src/components/horizontal-nav/horizontal-nav.i18n';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { useNavActions } from '../hooks';

import { useTopNavData } from './hooks';

export const TopNavHelp = memo((): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const { onKeyboardShortcutsActivated, onFeedbackModalOpen } = useNavActions();
  const {
    whatsNewUrl,
    attributionsUrl,
    supportAdminUrl,
    userUuid,
    currentUser,
  } = useTopNavData();

  const { blogs, newBlogCount, storeLastSeenBlog } = useBlogs(
    whatsNewUrl,
    userUuid || 'anonymous'
  );
  const isAtlassian = currentUser?.is_staff;

  const popupContent: () => JSX.Element = useMemo(
    () => () =>
      (
        <HelpMenu
          blogs={blogs}
          attributionsUrl={attributionsUrl}
          supportAdminUrl={supportAdminUrl}
          newBlogCount={newBlogCount}
          storeLastSeenBlog={storeLastSeenBlog}
          onKeyboardShortcutsActivated={onKeyboardShortcutsActivated}
          onFeedbackModalOpen={onFeedbackModalOpen}
          publishUiEvent={publishUiEvent}
          isAtlassian={isAtlassian}
        />
      ),
    [
      blogs,
      attributionsUrl,
      supportAdminUrl,
      newBlogCount,
      storeLastSeenBlog,
      onKeyboardShortcutsActivated,
      onFeedbackModalOpen,
      isAtlassian,
    ]
  );

  const {
    isEnabled,
    hasAssetOverrides,
    hasExperimentOverrides,
    hasFeatureOverrides,
    isBadgeVisible,
  } = useContext(DevToolsContext);
  const badge = useMemo(() => {
    if (
      isEnabled &&
      isBadgeVisible &&
      (hasAssetOverrides || hasFeatureOverrides || hasExperimentOverrides)
    ) {
      // render the star badge
      const style =
        hasExperimentOverrides || hasFeatureOverrides
          ? { color: token('color.text.accent.red') }
          : undefined;
      return () => <Badge style={style}>{hasAssetOverrides ? '★' : '☆'}</Badge>;
    }
    if (newBlogCount > 0) {
      // render the new blog count badge
      return () => <Badge>{newBlogCount}</Badge>;
    }
    return undefined;
  }, [
    isEnabled,
    isBadgeVisible,
    hasAssetOverrides,
    hasExperimentOverrides,
    hasFeatureOverrides,
    newBlogCount,
  ]);

  const HelpTrigger = useCallback(
    (triggerProps: TriggerProps) => {
      return (
        <Help
          isSelected={isOpen}
          onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)}
          label={intl.formatMessage(messages.helpTooltip)}
          testId="top-nav-help-button"
          {...triggerProps}
          badge={badge}
        />
      );
    },
    [intl, isOpen, badge]
  );

  return (
    <Popup
      placement="bottom-start"
      content={popupContent}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      trigger={HelpTrigger}
      testId="top-nav-help-menu"
    />
  );
});
